import { auth } from '../firebase';
import { BatchInstrumentCandidate, BatchInstrumentResolveResult, InstrumentSearchResult, ResolvedInstrument } from '../types/instruments';

const endpointBase =
    import.meta.env?.VITE_INSTRUMENT_ENDPOINT_BASE_URL ||
    import.meta.env?.VITE_MARKET_DATA_ENDPOINT_BASE_URL ||
    import.meta.env?.VITE_QUOTE_ENDPOINT_BASE_URL ||
    '';

const getAuthHeader = async () => {
    const user = auth.currentUser;
    if (!user) {
        throw new Error('You must be signed in to search instruments.');
    }
    const token = await user.getIdToken();
    return `Bearer ${token}`;
};

const toUserFriendlyInstrumentError = (status: number, body: string, fallback: string): Error => {
    const compactBody = body.replace(/\s+/g, ' ').trim();
    const lower = compactBody.toLowerCase();

    if (
        status === 401 ||
        status === 403 ||
        lower.includes('missing or insufficient permissions') ||
        lower.includes('permission-denied')
    ) {
        return new Error('Your session no longer has access for instrument lookup. Please sign out and sign back in, then try again.');
    }

    if (status === 429) {
        return new Error('Too many instrument lookups right now. Please wait a moment and try again.');
    }

    return new Error(fallback);
};

const getFunctionUrl = (functionName: 'searchInstruments' | 'resolveInstrument' | 'resolveInstrumentsBatch' | 'upsertInstrumentQuoteUnit') => {
    if (!endpointBase) {
        throw new Error('Missing API endpoint base URL for instrument search.');
    }

    try {
        const url = new URL(endpointBase);
        // Cloud Run function hostnames usually start with the function name,
        // e.g. https://getquote-<hash>-<region>.a.run.app.
        if (url.hostname.endsWith('.a.run.app') && url.hostname.includes('-')) {
            const hostParts = url.hostname.split('-');
            hostParts[0] = functionName.toLowerCase();
            url.hostname = hostParts.join('-');
            url.pathname = '/';
            url.search = '';
            return url.toString();
        }
    } catch {
        // Fall through to path-based URL construction for non-standard endpoint formats.
    }

    return `${endpointBase.replace(/\/+$/, '')}/${functionName}`;
};

export const searchInstruments = async (q: string, limit = 10): Promise<InstrumentSearchResult[]> => {
    const query = q.trim();
    if (query.length < 2) return [];

    const url = new URL(getFunctionUrl('searchInstruments'));
    // Send both params for compatibility with currently deployed backend variants.
    url.searchParams.set('instrumentKey', query);
    url.searchParams.set('q', query);
    url.searchParams.set('limit', String(limit));

    const authHeader = await getAuthHeader();
    const response = await fetch(url.toString(), {
        headers: {
            Authorization: authHeader,
        },
    });

    if (!response.ok) {
        const body = await response.text().catch(() => '');
        throw toUserFriendlyInstrumentError(
            response.status,
            body,
            `Instrument search failed (${response.status}): ${body.slice(0, 160)}`
        );
    }

    const data = await response.json();
    return Array.isArray(data?.results) ? data.results : [];
};

export const resolveInstrument = async (selection: {
    source: InstrumentSearchResult['source'];
    providerSymbol: string;
    symbol?: string;
    isin?: string;
    exchange?: string;
    currency?: string;
    priceCurrency?: string;
    totalCurrency?: string;
    costCurrency?: string;
    manualPriceCurrency?: string;
    manualQuoteUnit?: string;
    lseMajorUnitConfirmed?: boolean;
}): Promise<ResolvedInstrument> => {
    const authHeader = await getAuthHeader();
    const response = await fetch(getFunctionUrl('resolveInstrument'), {
        method: 'POST',
        headers: {
            Authorization: authHeader,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(selection),
    });

    if (!response.ok) {
        const body = await response.text().catch(() => '');
        throw toUserFriendlyInstrumentError(
            response.status,
            body,
            `Instrument resolve failed (${response.status}): ${body.slice(0, 160)}`
        );
    }

    const data = await response.json();
    if (!data?.instrument) {
        throw new Error('Instrument resolve returned no instrument payload.');
    }

    return data.instrument as ResolvedInstrument;
};

export const resolveInstrumentsBatch = async (candidates: BatchInstrumentCandidate[]): Promise<BatchInstrumentResolveResult[]> => {
    const authHeader = await getAuthHeader();
    const response = await fetch(getFunctionUrl('resolveInstrumentsBatch'), {
        method: 'POST',
        headers: {
            Authorization: authHeader,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ candidates }),
    });

    if (!response.ok) {
        const body = await response.text().catch(() => '');
        throw toUserFriendlyInstrumentError(
            response.status,
            body,
            `Instrument batch resolve failed (${response.status}): ${body.slice(0, 160)}`
        );
    }

    const data = await response.json();
    return Array.isArray(data?.results) ? (data.results as BatchInstrumentResolveResult[]) : [];
};


export const upsertInstrumentQuoteUnit = async (
    instrumentId: string,
    quoteUnit: string,
    unitScale: 1 | 0.01,
    currency: string
): Promise<void> => {
    const authHeader = await getAuthHeader();
    const response = await fetch(getFunctionUrl('upsertInstrumentQuoteUnit'), {
        method: 'POST',
        headers: {
            Authorization: authHeader,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ instrumentId, quoteUnit, unitScale, currency }),
    });

    if (!response.ok) {
        const body = await response.text().catch(() => '');
        throw toUserFriendlyInstrumentError(
            response.status,
            body,
            `Instrument quote-unit update failed (${response.status}): ${body.slice(0, 160)}`
        );
    }
};
